import React, { useEffect, useState, useCallback } from "react";
import LandscapeProgramLayout from "../LandscapeProgramLayout";
import { useAuthContext } from "../../../context/auth";
import BottomNav from "../BottomNav";
import api from "../../../lib/api";
import { HiroxSessionsDetailData } from "../../../Api";

const DIFFICULTY_GRADIENTS: Record<string, string> = {
  EASY: "linear-gradient(135deg, #E3FAFF 0%, #53CAF5 100%)",
  NORMAL: "linear-gradient(135deg, #FFF5E0 0%, #F6B731 100%)",
  HARD: "linear-gradient(135deg, #FFEBEB 0%, #FF5757 100%)",
};

const DIFFICULTY_LABELS: Record<string, string> = {
  EASY: "Easy",
  NORMAL: "Normal",
  HARD: "Hard",
};

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

function HiluxRanking() {
  const { hiluxSessionId } = useAuthContext();
  const [teamsData, setTeamsData] =
    useState<HiroxSessionsDetailData | null>(null);

  const fetchTeams = useCallback(() => {
    if (!hiluxSessionId) return;
    api.fits.hiroxSessionsDetail(hiluxSessionId).then((res) => {
      setTeamsData(res.data);
    });
  }, [hiluxSessionId]);

  useEffect(() => {
    fetchTeams();
  }, [fetchTeams]);

  const getRank = (team: { teamNumber: number; difficulty: string; time: number | null }): number | null => {
    if (!teamsData?.teams || team.time === null) return null;
    const sameDifficulty = teamsData.teams
      .filter((t) => t.difficulty === team.difficulty && t.time !== null)
      .sort((a, b) => a.time! - b.time!);
    const idx = sameDifficulty.findIndex((t) => t.teamNumber === team.teamNumber);
    return idx >= 0 ? idx + 1 : null;
  };

  return (
    <LandscapeProgramLayout step={5}>
      <div className="h-full w-full flex relative">
        <div className="p-[60px] flex flex-col gap-[40px] items-center w-full">
          <div className="flex items-center justify-center w-full">
            <h1 className="gradient-text text-[70px] font-bold">RANKING</h1>
          </div>
          <div className="grid grid-cols-4 gap-x-[30px] gap-y-[24px] w-full">
            {Array.from({ length: 8 }, (_, stIdx) => {
              const team = teamsData?.teams.find(
                (t) => t.teamNumber === stIdx + 1
              );
              const rank = team ? getRank(team) : null;
              return (
                <div
                  key={stIdx}
                  className="flex flex-col gap-[16px] items-center"
                >
                  <div className="flex items-center justify-between w-full">
                    <h2
                      className="text-[36px] font-extrabold text-white uppercase"
                      style={{
                        textShadow: "0px 0px 30px rgba(0,0,0,0.25)",
                      }}
                    >
                      TEAM {stIdx + 1}
                    </h2>
                    {team && (
                      <span
                        className="text-[24px] font-extrabold capitalize"
                        style={{
                          backgroundImage:
                            DIFFICULTY_GRADIENTS[team.difficulty],
                          backgroundClip: "text",
                          WebkitBackgroundClip: "text",
                          color: "transparent",
                        }}
                      >
                        {DIFFICULTY_LABELS[team.difficulty]}
                      </span>
                    )}
                  </div>
                  <div className="relative p-[20px] pb-[72px] gap-x-[30px] gap-y-[20px] flex flex-wrap bg-white/[0.12] min-h-[260px] max-h-[260px] w-full rounded-[12px] overflow-y-auto scrollbar-hide items-start content-start">
                    {team?.members.map((member) => (
                      <div
                        className="flex items-center gap-[12px]"
                        key={member.id}
                      >
                        <img
                          src={
                            member.profileImageUrl ||
                            "/defaultProfileImage.png"
                          }
                          alt="profile"
                          className="w-[36px] h-[36px] rounded-full"
                        />
                        <span className="text-[18px] text-white text-center">
                          {member.name}
                        </span>
                      </div>
                    ))}
                    {team && team.time !== null && (
                      <div className="absolute bottom-[20px] left-[20px] right-[20px] flex items-center justify-between">
                        <span className="text-[30px] font-medium text-white">
                          {formatTime(team.time)}
                        </span>
                        {rank && (
                          <span
                            className="text-[30px] font-medium capitalize"
                            style={{
                              backgroundImage:
                                DIFFICULTY_GRADIENTS[team.difficulty],
                              backgroundClip: "text",
                              WebkitBackgroundClip: "text",
                              color: "transparent",
                            }}
                          >
                            {rank}위
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <BottomNav />
    </LandscapeProgramLayout>
  );
}

export default HiluxRanking;
